import { useRef, useState, type ReactElement } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Receipt, Menu, Camera } from 'lucide-react';
import { useCompany } from './CompanyProvider';
import { useToast, errMsg } from './ToastProvider';
import { uploadReceiptFiles } from './receipts/scan';

/**
 * Untere Tab-Leiste für Mobile/App (Capacitor). Nur auf schmalen Viewports
 * sichtbar (md:hidden) — die Sidebar übernimmt am Desktop.
 * Mitte: großer Kamera-Button für den Beleg-Scan, der von überall die Kamera
 * öffnet, das Foto direkt hochlädt und die KI-Erkennung anstößt.
 */
export default function MobileTabBar(): ReactElement {
  const { active } = useCompany();
  const toast = useToast();
  const navigate = useNavigate();
  const camRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const list = Array.from(e.target.files ?? []);
    if (camRef.current) camRef.current.value = '';
    if (list.length === 0 || !active) return;
    setBusy(true);
    try {
      const created = await uploadReceiptFiles(active.id, list);
      toast.success(`${created} Beleg(e) erfasst – KI erkennt im Hintergrund.`);
      navigate('/app/belege');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const tab = 'flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium';
  const active_ = 'text-blue-600';
  const idle = 'text-gray-500';

  return (
    <>
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onCapture}
      />
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] dark:border-gray-700 dark:bg-gray-800 md:hidden">
        <NavLink to="/app" end className={({ isActive }) => `${tab} ${isActive ? active_ : idle}`}>
          <LayoutDashboard className="h-5 w-5" />
          Home
        </NavLink>
        <NavLink to="/app/rechnungen" className={({ isActive }) => `${tab} ${isActive ? active_ : idle}`}>
          <FileText className="h-5 w-5" />
          Rechnungen
        </NavLink>

        {/* Zentraler Kamera-Button (Beleg-Scan) */}
        <div className="flex flex-1 justify-center">
          <button
            type="button"
            onClick={() => camRef.current?.click()}
            disabled={busy}
            aria-label="Beleg scannen"
            className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 transition active:scale-95 disabled:opacity-60"
          >
            {busy ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Camera className="h-6 w-6" />
            )}
          </button>
        </div>

        <NavLink to="/app/belege" className={({ isActive }) => `${tab} ${isActive ? active_ : idle}`}>
          <Receipt className="h-5 w-5" />
          Belege
        </NavLink>
        <NavLink to="/app/mehr" className={({ isActive }) => `${tab} ${isActive ? active_ : idle}`}>
          <Menu className="h-5 w-5" />
          Mehr
        </NavLink>
      </nav>
    </>
  );
}
